import React, { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL || 'https://didrouteapi.com/api';

const countries = [
  "United States",
  "United Kingdom",
  "Australia",
  "India",
  // Add more countries as needed
];

const CampaignForm = ({ campaign, onClose, onSave, availableNumbers = [] }) => {
  const [formData, setFormData] = useState({
    campaign_name: campaign?.campaign_name || "",
    country: campaign?.country || "",
    target_numbers: campaign?.target_number ? [campaign.target_number] : [],
    recording: campaign?.recording || "Yes",
    weight_flag: campaign?.weight_flag || "No",
    dupe: campaign?.dupe || "No",
    dupe_handle: campaign?.dupe_handle || "",
  });

  const [selectedNumber, setSelectedNumber] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allCampaigns, setAllCampaigns] = useState([]);

  // Fetch all campaigns when component mounts
  useEffect(() => {
    const fetchAllCampaigns = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/campaigns`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.success) {
          setAllCampaigns(data.data);
        }
      } catch (error) {
        console.error('Error fetching campaigns:', error);
      }
    };

    fetchAllCampaigns();
  }, []);

  // Filter out already selected numbers from available numbers
  const availableNumbersFiltered = availableNumbers.filter(
    number => !formData.target_numbers.includes(number.did_number)
  );

  const validateTargetNumbers = (numbers) => {
    if (!numbers || numbers.length === 0) return true;
    
    return numbers.every(number => {
      const isNumberInUse = allCampaigns.some(existingCampaign => 
        existingCampaign.target_number === number && 
        existingCampaign.id !== campaign?.id
      );
      return !isNumberInUse;
    });
  };

  const handleAddNumber = () => {
    if (selectedNumber && !formData.target_numbers.includes(selectedNumber)) {
      if (validateTargetNumbers([selectedNumber])) {
        setFormData(prev => ({
          ...prev,
          target_numbers: [...prev.target_numbers, selectedNumber]
        }));
        setSelectedNumber("");
        setError("");
      } else {
        setError("This number is already assigned to another campaign");
      }
    }
  };

  const handleRemoveNumber = (numberToRemove) => {
    setFormData(prev => ({
      ...prev,
      target_numbers: prev.target_numbers.filter(num => num !== numberToRemove)
    }));
    setError("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError("");
  };

  const validateForm = () => {
    if (!formData.campaign_name.trim()) {
      setError("Campaign Name is required");
      return false;
    }
    if (!formData.country.trim()) {
      setError("Country is required");
      return false;
    }

    // Require at least one number
    if (formData.target_numbers.length === 0) {
      setError("Please add at least one number");
      return false;
    }

    // Validate selected numbers
    if (!validateTargetNumbers(formData.target_numbers)) {
      setError("One or more selected numbers are already assigned to other campaigns");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const submitData = {
        campaign_name: formData.campaign_name.trim(),
        country: formData.country.trim(),
        target_numbers: formData.target_numbers,
        recording: formData.recording,
        weight_flag: formData.weight_flag,
        dupe: formData.dupe,
        dupe_handle: formData.dupe === "Yes" ? formData.dupe_handle : null,
      };

      await onSave(submitData);
      onClose();
    } catch (error) {
      setError('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-start justify-center z-50 overflow-y-auto p-4 md:p-6">
      <div className="bg-white p-4 rounded-lg shadow-xl w-full max-w-md my-8">
        <h2 className="text-black font-bold mb-4">
          {campaign ? "Edit Campaign" : "Add Campaign"}
        </h2>

        {error && (
          <div className="mb-4 p-2 bg-red-400 text-black rounded">{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-900 text-sm mb-1">
                Campaign Name *
              </label>
              <input
                type="text"
                name="campaign_name"
                value={formData.campaign_name}
                onChange={handleInputChange}
                className="w-full bg-gray-200 placeholder:text-gray-700 text-md text-gray-900 p-2 rounded"
                required
              />
            </div>

            <div>
              <label className="block text-gray-900 text-sm mb-1">
                Country *
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full bg-gray-200 placeholder:text-gray-700 text-md text-gray-900 p-2 rounded"
                required
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-gray-900 text-sm mb-1">
                Select Numbers
              </label>
              
              <div className="flex space-x-2">
                <select
                  value={selectedNumber}
                  onChange={(e) => setSelectedNumber(e.target.value)}
                  className="flex-1 bg-gray-200 text-gray-900 p-2 rounded"
                >
                  <option value="">Select a number</option>
                  {availableNumbersFiltered.map((number) => (
                    <option key={number.id} value={number.did_number}>
                      {number.did_number}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleAddNumber}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Add
                </button>
              </div>

              <div className="mt-2">
                <label className="block text-gray-900 text-sm mb-1">
                  Selected Numbers:
                </label>
                <div className="space-y-2">
                  {formData.target_numbers.map((number) => (
                    <div
                      key={number}
                      className="flex items-center justify-between bg-gray-100 p-2 rounded"
                    >
                      <span className="text-gray-900">{number}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveNumber(number)}
                        className="text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-gray-900 text-sm">
                Recording <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center text-gray-900">
                  <input
                    type="radio"
                    name="recording"
                    value="Yes"
                    checked={formData.recording === "Yes"}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Yes
                </label>
                <label className="flex items-center text-gray-900">
                  <input
                    type="radio"
                    name="recording"
                    value="No"
                    checked={formData.recording === "No"}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  No
                </label>
              </div>
            </div>

            {campaign && (
              <div>
                <label className="block text-gray-900 text-sm">
                  Weight <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center text-gray-900">
                    <input
                      type="radio"
                      name="weight_flag"
                      value="Yes"
                      checked={formData.weight_flag === "Yes"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center text-gray-900">
                    <input
                      type="radio"
                      name="weight_flag"
                      value="No"
                      checked={formData.weight_flag === "No"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
                {formData.weight_flag === "Yes" && (
                  <div className="mt-2 p-2 bg-yellow-100 text-yellow-800 rounded text-sm">
                    Note: Please ensure that the total weight across all your targets equals 100%.
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-gray-900 text-sm">
                Dupe <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center text-gray-900">
                  <input
                    type="radio"
                    name="dupe"
                    value="Yes"
                    checked={formData.dupe === "Yes"}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Yes
                </label>
                <label className="flex items-center text-gray-900">
                  <input
                    type="radio"
                    name="dupe"
                    value="No"
                    checked={formData.dupe === "No"}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  No
                </label>
              </div>
            </div>

            {formData.dupe === "Yes" && (
              <div>
                <label className="block text-gray-900 text-sm">
                  Dupe Handle
                </label>
                <select
                  name="dupe_handle"
                  value={formData.dupe_handle}
                  onChange={handleInputChange}
                  className="w-full bg-gray-200 placeholder:text-gray-700 text-md text-gray-900 p-1 rounded"
                  required
                >
                  <option value="">Select Option</option>

                  <option value="Same Target">Same Target</option>
                  <option value="Different Target">Different Target</option>
                  <option value="Random Target">Random Target</option>
                </select>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-4">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignForm;
